import {
  Component,
  Input,
  OnInit,
  ViewChild,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import {
  PerfectScrollbarConfigInterface,
  PerfectScrollbarComponent,
} from 'ngx-perfect-scrollbar';
import { truncate, eq, find } from 'lodash';
import { Store } from '@ngrx/store';
import { AppState } from 'app/app.reducer';
import * as actions from 'redux/actions';
import { FacetSearch, Project, ContentItems, Search } from 'app/models';
import { AuthorService } from 'app/services';
import { Content, STAR_ICON, STAR_ICON_FILL, SPINNER_ICON } from 'app/constants';
import { ToastNotificationService } from 'app/components/toast-notification/toast-notification.service';

@Component({
  selector: 'app-content-items',
  templateUrl: './content-items.component.html',
  styleUrls: ['./content-items.component.scss'],
})
export class ContentItemsComponent implements OnInit, OnChanges {
  @Input() parentName: string;
  @Input() items: any[];
  @Input() loading: boolean;
  @Input() pageSize: number;
  @Input() page: number;
  @Input() total: number;
  @Input() keywords: string;
  public contentItems: ContentItems[];
  public perfectScrollbarHeight: any;
  public section: string;
  public sliceControl: number;
  public screenSize: number;
  public starUnSelected: string;
  public starSelected: string;
  public project: Project;
  public contentItemsSaved: ContentItems[];
  public searchBody: Search;
  public tooltipText: string;
  public tooltipVisible: boolean;
  public tooltipX: number;
  public tooltipY: number;
  public savingItemId: number | null;
  public savingAuthorId: number | null;
  public savingOutletId: number | null;
  public authorsSaved: any[];
  public outletsSaved: any[];

  public config: PerfectScrollbarConfigInterface = {};

  @ViewChild('contentItemsPerfectScroll', { static: false })
  contentItemsPerfectScroll: PerfectScrollbarComponent;
  constructor(
    private store: Store<AppState>,
    private authorService: AuthorService,
    private notificationService: ToastNotificationService
  ) {
    this.starUnSelected = STAR_ICON;
    this.starSelected = STAR_ICON_FILL;
    this.sliceControl = 80;
    this.contentItems = [];
    this.contentItemsSaved = [];
    this.tooltipText = '';
    this.tooltipVisible = false;
    this.tooltipX = 0;
    this.tooltipY = 0;
    this.savingItemId = null;
    this.savingAuthorId = null;
    this.savingOutletId = null;
    this.authorsSaved = [];
    this.outletsSaved = [];
    this.perfectScrollbarHeight = {
      height: 'calc(100vh - 205px)',
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.items && changes.items.currentValue) {
      this.contentItems = changes.items.currentValue.map(
        (item) =>
          new ContentItems({
            ...item,
            truncateTextTitleExcerpt: this.textTrucate(item.titleExcerpt),
            truncateTextTitle: this.textTrucate(item.title),
            isSaved: this.checkIsSavedContentItemToProject(item.id),
          })
      );
      this.scrollTop();
    }
    if (changes.parentName && changes.parentName.currentValue) {
      this.section = changes.parentName.currentValue;
      switch (changes.parentName.currentValue) {
        case Content.NewsFeed:
          this.perfectScrollbarHeight.height = 'calc(100vh - 230px)';
          break;
        case Content.Coverage:
          this.perfectScrollbarHeight.height = 'calc(100vh - 285px)';
          break;
        case Content.Author:
          this.perfectScrollbarHeight.height = 'calc(100vh - 300px)';
          break;
        case Content.Outlet:
          this.perfectScrollbarHeight.height = 'calc(100vh - 300px)';
          break;
        case Content.Topic:
          this.perfectScrollbarHeight.height = 'calc(100vh - 500px)';
          break;
        case Content.Company:
          this.perfectScrollbarHeight.height = 'calc(100vh - 450px)';
          break;
        case Content.TopicSidebar:
          this.perfectScrollbarHeight.height = 'calc(100vh - 340px)';
          break;
        case Content.Collection:
          this.perfectScrollbarHeight.height = 'calc(100vh - 285px)';
          break;
      }
    }
  }

  ngOnInit() {
    this.store.select('ui').subscribe(({ screenSizeValues }) => {
      if (eq(this.section, Content.Coverage)) {
        this.screenSize = screenSizeValues.scrWidth;
        if (this.screenSize <= 1299 && this.screenSize >= 500) {
          this.sliceControl = 40;
        }
        if (this.screenSize <= 1699 && this.screenSize >= 1300) {
          this.sliceControl = 50;
        }
        if (this.screenSize <= 1921 && this.screenSize >= 1700) {
          this.sliceControl = 70;
        }
        if (this.screenSize >= 1922) {
          this.sliceControl = 80;
        }
        this.updateTitleLength();
      }
    });

    this.store.select('projects').subscribe(({ projects, action }) => {
      if (!!projects.length) {
        this.project = projects.find((item) => item.selected);
      }
    });

    this.store.select('project').subscribe(({ projectSelected }) => {
      if (projectSelected) {
        this.project = projectSelected;
      }
    });

    this.store.select('contentItemsList').subscribe((
      { contentItems, savedError, deletedError, isAddingContentItem, isDeletingContentItem }
    ) => {
      this.contentItemsSaved = contentItems;
      if (this.contentItems.length > 0) {
        this.contentItems = this.contentItems.map(
          (item) =>
            new ContentItems({
              ...item,
              isSaved: this.checkIsSavedContentItemToProject(item.id),
            })
        );
      }
      if (!isAddingContentItem && !isDeletingContentItem) {
        this.savingItemId = null;
      }
      if (savedError) {
        this.notificationService.showMessage(
          'Error saving clip to project. Please try again.', 'Error'
        );
      }
      if (deletedError) {
        this.notificationService.showMessage(
          'Error removing clip from project. Please try again.', 'Error'
        );
      }
    });

    this.store
      .select('authorsOutletsList')
      .subscribe(({ authors, outlets, isAddingAuthorOutlet, isDeletingAuthorOutlet, errors }) => {
        const wasSavingAuthorOutlet = !!this.savingAuthorId || !!this.savingOutletId;
        this.authorsSaved = authors || [];
        this.outletsSaved = outlets || [];
        if (errors?.message && wasSavingAuthorOutlet) {
          this.notificationService.showMessage(
            'Error updating author or outlet for project. Please try again.', 'Error'
          );
        }
        if (!isAddingAuthorOutlet && !isDeletingAuthorOutlet) {
          this.savingAuthorId = null;
          this.savingOutletId = null;
        }
      });

    this.store.select('search').subscribe(({ searchBody }) => {
      if (searchBody) {
        this.searchBody = searchBody;
      }
    });
  }

  isAuthorSaved(item): boolean {
    return !!find(this.authorsSaved, (author) => eq(author.partyId, item.authorId));
  }

  isOutletSaved(item): boolean {
    return !!find(this.outletsSaved, (outlet) => eq(outlet.partyId, item.outletId));
  }

  checkIsSavedContentItemToProject(checkContentItemId): boolean {
    if (this.contentItemsSaved.length === 0) {
      return false;
    }
    return this.contentItemsSaved.find(({ contentItemId }) => eq(contentItemId, checkContentItemId))
      ? true
      : false;
  }

  textTrucate(text) {
    if (text) {
      if (eq(this.section, Content.Coverage)) {
        if (text.length >= this.sliceControl) {
          return truncate(text, {
            length: this.sliceControl,
            omission: '...',
          });
        }
      }
    }
    return text;
  }

  updateTitleLength() {
    const items = this.contentItems.map((item) => ({
      ...item,
      truncateTextTitleExcerpt: this.textTrucate(item.titleExcerpt),
      truncateTextTitle: this.textTrucate(item.title),
    }));
    this.contentItems = items;
  }

  goToTitleUrl(url) {
    window.open(url, '_blank');
  }

  getAuthorContent(authorItem) {
    const facetSearch = new FacetSearch({
      searchBody: new Search({
        ...this.searchBody,
        authors: [authorItem.authorId],
      }),
    });

    this.store.dispatch(
      actions.loadAuthorContent({
        facetSearch,
        author: {
          ...authorItem,
          id: authorItem.authorId,
          name: authorItem.author,
          outletNames: authorItem.outlet,
        },
      })
    );
  }

  getOutletContent(outlet) {
    const facetSearch = new FacetSearch({
      searchBody: new Search({
        ...this.searchBody,
        outlets: [outlet.outletId],
      }),
    });

    this.store.dispatch(
      actions.loadOutletContent({
        facetSearch,
        outlet: {
          ...outlet,
          id: outlet.outletId,
          name: outlet.outlet,
        },
      })
    );
  }

  private scrollTop() {
    if (this.contentItemsPerfectScroll) {
      this.contentItemsPerfectScroll.directiveRef.scrollTo(0, 1);
    }
  }

  showTooltip(event: MouseEvent, text: string) {
    this.tooltipText = text;
    this.tooltipX = event.clientX;
    this.tooltipY = event.clientY - 10;
    this.tooltipVisible = true;
  }

  hideTooltip() {
    this.tooltipVisible = false;
  }

  public getStarIcon(item): string {
    if (this.savingItemId === item.id) {
      return SPINNER_ICON;
    }
    return item.isSaved ? this.starSelected : this.starUnSelected;
  }

  public handleSaveContenItem(item) {
    this.savingItemId = item.id;
    if (item.isSaved) {
      this.store.dispatch(
        actions.deleteContentItemToProject({
          projectId: this.project.value,
          contentItemId: item.id,
        })
      );
    } else {
      this.store.dispatch(
        actions.saveContentItemToProject({
          projectId: this.project.value,
          contentItemId: item.id,
        })
      );
    }
  }

  public onAuthorStarToggled(item, currentActive: boolean): void {
    this.savingAuthorId = item.authorId;
    const userProject = { value: this.project.value };
    const author = { id: item.authorId, name: item.author };
    if (currentActive) {
      this.store.dispatch(
        actions.deleteAuthorToProject({ partyId: item.authorId, userProject, author })
      );
    } else {
      this.store.dispatch(
        actions.addAuthorToProject({ partyId: item.authorId, userProject, author })
      );
    }
  }

  public onOutletStarToggled(item, currentActive: boolean): void {
    this.savingOutletId = item.outletId;
    const userProject = { value: this.project.value };
    const outlet = { id: item.outletId, name: item.outlet };
    if (currentActive) {
      this.store.dispatch(
        actions.deleteOutletToProject({ partyId: item.outletId, userProject, outlet })
      );
    } else {
      this.store.dispatch(
        actions.addOutletToProject({ partyId: item.outletId, userProject, outlet })
      );
    }
  }
}
